import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
    loadDarPriorities,
    loadDarRequestTypes,
    loadDarStatuses,
    loadServiceDetailsByService
} from '../../../store/directusService';
import {
    clearDarQueueSearch,
    clearParticipantsSearch,
    loadParticipantsAdvancedSearch,
    searchDarQueue
} from '../../../store/participantService';
import { parseDateRange } from '../../../utils/dateFunctions';
import { getNameFromId, getObjFromId } from '../../../utils/directusFunctions';
import { MAX_PTCODE } from '../../../utils/formValidation/commonConstants';
import {
    DAR_PRIORITIES_ERROR,
    DAR_REQUEST_TYPES_ERROR,
    DAR_STATUSES_ERROR,
    SERVICES_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { stableSort } from '../../../utils/sortFunctions';
import { addFullNameToArray } from '../../../utils/userArrayUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateRangePicker from '../../formElements/DateRangePicker';
import MultiSelect from '../../formElements/MultiSelect';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CreateDar from './CreateDar';
import DarChangeEditor from './DarChangeEditor';
import DarTableRow from './DarTableRow';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from '../homeStyles/dar.module.css';

const headCells = [
    { id: 'code', numeric: false, label: 'DAR ID' },
    { id: 'serviceName', numeric: false, label: 'Service' },
    { id: 'dateCreated', numeric: false, label: 'Date Created' },
    { id: 'dateUpdated', numeric: false, label: 'Last Updated' },
    { id: 'ptCode', numeric: false, label: 'PT ID' },
    { id: 'priorityName', numeric: false, label: 'Priority' },
    { id: 'statusName', numeric: false, label: 'Status' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'statusName',
    page: 0,
    rowsPerPage: 100
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const ownerRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER];

const DarTableManagement = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const { state } = useLocation();
    const [rows, setRows] = useState([]);
    const [participantIds, setParticipantIds] = useState([]);
    const [filters, setFilters] = useState({});
    const [ptCode, setPtCode] = useState('');
    const [isOpenDarChangeEditor, setIsOpenDarChangeEditor] = useState(false);
    const [darRow, setDarRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [owners, setOwners] = useState([]);
    const [keys, setKeys] = useState({
        priorityId: '0',
        statusId: '1',
        serviceId: '2',
        requestTypeId: '3',
        userId: '4'
    });
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [errors, setErrors] = useState({});
    const [openId, setOpenId] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const usersMetaData = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIdsMetaData
    );
    const loadingUsers = useSelector(
        (state) => state.entities.userService.loadingUsersByLoggedInUserServiceIds
    );
    const darPriorities = useSelector((state) => state.entities.directusService.darPriorities);
    const darRequestTypes = useSelector((state) => state.entities.directusService.darRequestTypes);
    const darStatuses = useSelector((state) => state.entities.directusService.darStatuses);
    const serviceDetails = useSelector((state) => state.entities.directusService.serviceDetails);
    const participantsById = useSelector(
        (state) => state.entities.participantService.participantsById
    );
    const participantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const loadingParticipants = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const darQueuesSearch = useSelector(
        (state) => state.entities.participantService.darQueuesSearch
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // USEEFFECTS
    useEffect(() => {
        darPriorities?.length < 1 && dispatch(loadDarPriorities());
        darStatuses?.length < 1 && dispatch(loadDarStatuses());
        darRequestTypes?.length < 1 && dispatch(loadDarRequestTypes());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!loggedInUser || !loggedInUser?.serviceIds) return;
        if (loggedInUser?.serviceIds?.length > 0) {
            dispatch(loadServiceDetailsByService(loggedInUser.serviceIds));
        }
    }, [loggedInUser.id]);

    useEffect(() => {
        if (loadingUsers || !usersMetaData?.last) return;
        const newOwners = users.filter((el) =>
            el.userTypes.some((entry) => ownerRoles.includes(entry.role))
        );
        setOwners(addFullNameToArray(newOwners));
    }, [users, usersMetaData, loadingUsers]);

    useEffect(() => {
        const cleanedFilters = Object.entries(filters)
            // eslint-disable-next-line
            .filter(([_, val]) => (Array.isArray(val) ? val.length > 0 : val))
            .reduce((acc, cur) => ({ ...acc, [cur[0]]: cur[1] }), {});
        if (Object.values(cleanedFilters).length > 0) dispatch(searchDarQueue(cleanedFilters));
        else {
            setRows([]);
        }
    }, [filters]);

    useEffect(() => {
        if (!darQueuesSearch || darQueuesSearch?.length < 1) {
            setRows([]);
            return;
        }
        const requiredParticipantIds = [...new Set(darQueuesSearch.map((el) => el.participantId))];
        const missingIds = requiredParticipantIds.filter((el) => !(el in participantsById));
        if (missingIds.length) {
            dispatch(loadParticipantsAdvancedSearch({ ids: missingIds }, 0));
            setParticipantIds(missingIds);
        }
    }, [darQueuesSearch, participantsById]);

    useEffect(() => {
        if (
            !loadingParticipants &&
            participantsSearch?.length > 0 &&
            !participantsSearchMetaData.last &&
            !!participantIds.length &&
            Object.keys(participantsById)?.length
        ) {
            const number = participantsSearchMetaData?.number + 1 || 0;
            dispatch(loadParticipantsAdvancedSearch({ ids: participantIds }, number));
        }
    }, [
        participantsSearch,
        participantsSearchMetaData,
        loadingParticipants,
        participantIds,
        participantsById
    ]);

    useEffect(() => {
        if (
            !darQueuesSearch?.length ||
            !darStatuses?.length ||
            !darPriorities?.length ||
            !darRequestTypes?.length ||
            !serviceDetails?.length ||
            !Object.keys(participantsById)?.length
        )
            return;
        setRows(
            darQueuesSearch.map((el) => ({
                ...el,
                priorityName: getNameFromId(darPriorities, el.priorityId),
                statusName:
                    getNameFromId(darStatuses, el.statusId).toLowerCase() === 'closed'
                        ? `zz${getNameFromId(darStatuses, el.statusId)}`
                        : getNameFromId(darStatuses, el.statusId),
                requestTypeName: getNameFromId(darRequestTypes, el.requestTypeId),
                serviceName: getNameFromId(serviceDetails, el.serviceId),
                ptCode: participantsById[el.participantId]?.ptCode || ''
            }))
        );
    }, [
        darQueuesSearch,
        serviceDetails,
        darRequestTypes,
        darStatuses,
        darPriorities,
        participantsById
    ]);

    useEffect(() => {
        if (successMessage === `DAR has been added` || successMessage === `DAR has been updated`) {
            Object.values(filters).filter((el) => (Array.isArray(el) ? el.length > 0 : el)).length >
                0 && dispatch(searchDarQueue(filters));
        }
    }, [successMessage]);

    useEffect(() => {
        if (state?.darAccordion === 'participantValidation') {
            setPtCode(currentParticipant.ptCode);
            setFilters((filters) => ({ ...filters, ptCode: currentParticipant.ptCode }));
        }
    }, [currentParticipant]);

    // HELPER FNS
    const loadingError = () => {
        if (darPriorities?.length < 1) return DAR_PRIORITIES_ERROR;
        if (darRequestTypes?.length < 1) return DAR_REQUEST_TYPES_ERROR;
        if (darStatuses?.length < 1) return DAR_STATUSES_ERROR;
        if (serviceDetails?.length < 1) return SERVICES_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error) setErrors((errors) => ({ ...errors, [key]: '' }));
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <DarTableRow
                key={el.id}
                row={el}
                userRoles={loggedInUser.roles}
                acceptedRoles={acceptedRoles}
                onUpdateDarRow={onUpdateDarRow}
                toggleDropdown={setOpenId}
                openId={openId}
            />
        ));

    const cleanUp = () => {
        dispatch(clearDarQueueSearch());
        dispatch(clearParticipantsSearch());
        setRows([]);
        setFilters({});
        setKeys(clearKeys(keys));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        setFilters((filters) => ({ ...filters, [key]: value }));
    };

    const onSearchDateRange = (searchRange) => {
        const { dateFrom, dateTo } = parseDateRange(searchRange);
        setFilters((filters) => ({ ...filters, dateCreatedFrom: dateFrom, dateCreatedTo: dateTo }));
    };

    const onPtCodeChange = (ptCode) => {
        clearError('ptCode');
        if (ptCode.length === MAX_PTCODE || ptCode.length < 1) {
            setFilters((filters) => ({ ...filters, ptCode }));
        }
        setPtCode(ptCode);
    };

    const onCreateDar = () => {
        setFormType('create');
        setShowForm(true);
    };

    const onUpdateDarRow = (row) => {
        setDarRow(row);
        setIsOpenDarChangeEditor(true);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={form.formWrapper}>
            <form data-testid="dar_search_form">
                {state?.darAccordion === 'participantValidation' ? (
                    <h2 className={app.sectionHeading}>Data Amendment Requests</h2>
                ) : (
                    <>
                        <h2 className={app.sectionHeading}>Data Amendment Request Search:</h2>
                        <div className={form.formSection}>
                            <div className={form.formColumn}>
                                <SingleSelect
                                    id="priorityId"
                                    key={keys.priorityId}
                                    label="Priority"
                                    placeholder="Select a priority..."
                                    disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                                    menuItems={darPriorities || []}
                                    selectedId={filters.priorityId}
                                    selected={getObjFromId(darPriorities, filters.priorityId)}
                                    onChange={(chosenId) => onUpdate('priorityId', chosenId)}
                                />
                                <SingleSelect
                                    id="statusId"
                                    key={keys.statusId}
                                    label="Status"
                                    placeholder="Select a status..."
                                    disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                                    menuItems={darStatuses || []}
                                    selectedId={filters.statusId}
                                    selected={getObjFromId(darStatuses, filters.statusId)}
                                    onChange={(chosenId) => onUpdate('statusId', chosenId)}
                                />
                                <SingleSelect
                                    id="serviceId"
                                    key={keys.serviceId}
                                    label="Service"
                                    placeholder="Select a service..."
                                    disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                                    menuItems={serviceDetails || []}
                                    selectedId={filters.serviceId}
                                    selected={getObjFromId(serviceDetails, filters.serviceId)}
                                    onChange={(chosenId) => onUpdate('serviceId', chosenId)}
                                />
                                <MultiSelect
                                    id="userIds"
                                    label="DAR Owner"
                                    placeholder="Select DAR owners..."
                                    disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                                    menuItems={owners || []}
                                    preSelectedIds={filters.userIds}
                                    preSelects={
                                        filters?.userIds?.map((el) => getObjFromId(owners, el)) ||
                                        []
                                    }
                                    onChange={(chosenIds) => onUpdate('userIds', chosenIds)}
                                />
                            </div>
                            <div className={form.formColumn}>
                                <DateRangePicker
                                    id="dateCreated"
                                    label="Date Created"
                                    disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                                    onSearch={onSearchDateRange}
                                />
                                <SingleSelect
                                    id="requestTypeId"
                                    key={keys.requestTypeId}
                                    label="Request Type"
                                    placeholder="Select request type..."
                                    disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                                    menuItems={darRequestTypes || []}
                                    selectedId={filters.requestTypeId}
                                    selected={getObjFromId(darRequestTypes, filters.requestTypeId)}
                                    error={errors.requestTypeId}
                                    onChange={(chosenId) => onUpdate('requestTypeId', chosenId)}
                                />
                                <TextInputField
                                    id={'ptCode'}
                                    label={'PT ID'}
                                    placeholder={'Enter PT ID'}
                                    maxLength={MAX_PTCODE}
                                    value={ptCode || ''}
                                    error={errors.ptCode}
                                    onChange={(e) => onPtCodeChange(e.target.value.toUpperCase())}
                                />
                            </div>
                        </div>
                    </>
                )}
                { isOpenDarChangeEditor && (
                    <DarChangeEditor
                        isOpen={isOpenDarChangeEditor}
                        onClose={() => setIsOpenDarChangeEditor(false)}
                        row={darRow}
                    />
                )}

                {rows.length < 1 ? (
                    Object.values(filters).filter((el) => (Array.isArray(el) ? el.length > 0 : el))
                        .length < 1 ? (
                        'Select filters'
                    ) : (
                        <LoadingSpinner content="No DARs found" />
                    )
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        defaultRowsPerPage={rowMetaData.rowsPerPage}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </form>

            <div className={local.editWrapper}>
                {!showForm && (
                    <Button
                        id="darSearchButton"
                        content="Add a DAR"
                        disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                        icon={true}
                        onClick={onCreateDar}
                    />
                )}
                {showForm && formType === 'create' && (
                    <div className={app.fadein}>
                        <CreateDar
                            onClose={() => setShowForm(false)}
                            roles={loggedInUser.roles}
                            darRequestTypes={darRequestTypes}
                            darPriorities={darPriorities}
                            serviceDetails={serviceDetails}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DarTableManagement;
